var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var SegmentationConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import { ColumnFormat, CustomFormat, DefaultSettings } from './customFormat';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationState } from './validation/toolValidationResult';
const schema = {
    $id: 'urn:wisepipe-common-pipe:SegmentationConfig',
    type: 'object',
    properties: {
        target: { type: 'string', minLength: 1, maxLength: 150 },
        alias: { type: 'string', minLength: 1, maxLength: 150 },
        balanced: { type: 'boolean' },
        maxSegments: { type: ['integer', 'null', 'boolean'], if: { type: 'integer' }, then: { minimum: 2, maximum: 25 } },
        minSize: { type: ['number', 'null'], if: { type: 'number' }, then: { minimum: 0.01, maximum: 0.5 } },
        maxDepth: { type: ['integer', 'null'], if: { type: 'integer' }, then: { minimum: 2, maximum: 10 } },
        excludeColumns: { type: 'array', items: { type: 'string', minLength: 1 }, uniqueItems: true },
        autoExclude: { type: 'boolean' },
        autoExcludeCorrMethod: { type: 'string', enum: ['pearson', 'kendall', 'spearman'] },
        autoExcludeCorrThreshold: { type: 'number' },
        autoExcludePpsThreshold: { type: 'number' }
    },
    required: ['balanced', 'target', 'alias']
};
let SegmentationConfig = SegmentationConfig_1 = class SegmentationConfig extends ToolConfig {
    constructor(target, alias, balanced, maxSegments, maxDepth, excludeColumns, autoExclude, autoExcludeCorrMethod, autoExcludeCorrThreshold, autoExcludePpsThreshold, customFormats, minSize) {
        super(ToolType.SEGMENTATION, customFormats);
        this.target = target;
        this.alias = alias;
        this.balanced = balanced;
        if (maxSegments !== undefined)
            this.maxSegments = maxSegments;
        if (minSize !== undefined)
            this.minSize = minSize;
        if (maxDepth !== undefined)
            this.maxDepth = maxDepth;
        if (excludeColumns !== undefined)
            this.excludeColumns = excludeColumns;
        if (autoExclude !== undefined)
            this.autoExclude = autoExclude;
        if (autoExcludeCorrMethod !== undefined)
            this.autoExcludeCorrMethod = autoExcludeCorrMethod;
        if (autoExcludeCorrThreshold !== undefined)
            this.autoExcludeCorrThreshold = autoExcludeCorrThreshold;
        if (autoExcludePpsThreshold !== undefined)
            this.autoExcludePpsThreshold = autoExcludePpsThreshold;
        JSON.validate(this, SegmentationConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return SegmentationConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get data() {
        return SegmentationConfig_1.data;
    }
    // eslint-disable-next-line class-methods-use-this
    get segments() {
        return SegmentationConfig_1.segments;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return SegmentationConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return SegmentationConfig_1.outs;
    }
    static get in() {
        return SegmentationConfig_1.ins[0];
    }
    static get data() {
        return SegmentationConfig_1.outs[0];
    }
    static get segments() {
        return SegmentationConfig_1.outs[1];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['data', 'segments'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.SEGMENTATION.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.LEARN], this.ins, this.outs, ['segment'], true);
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        if (source !== undefined) {
            const dataExp = tsb.export(this.data);
            const dependents = this.copyAllInsAndReturnDependents(dataExp, this.in, source, [this.target]);
            dataExp.makeNew(new Column(this.alias, 'string'), dependents, true).setColumnFormats(this.customFormats);
            const srcColumn = source.find((c) => c.name === this.target);
            const targetType = srcColumn.type;
            const segExp = tsb.export(this.segments);
            const segColumns = [
                new Column(SegmentationConfig_1.SegmentIDColumn, 'number'),
                new Column(SegmentationConfig_1.NameColumn, 'string'),
                new Column(SegmentationConfig_1.DescriptionColumn, 'string'),
                new Column(SegmentationConfig_1.PredictionColumn, targetType),
                new Column(SegmentationConfig_1.ProbabilityColumn, 'number'),
                new Column(SegmentationConfig_1.FeatureColumn, 'string'),
                new Column(SegmentationConfig_1.OperatorColumn, 'string'),
                new Column(SegmentationConfig_1.ValueColumn, 'string')
            ];
            segColumns.forEach((c) => segExp.makeNew(c, dependents, true));
            const newColumnFormats = new Map();
            if (srcColumn?.customFormat) {
                newColumnFormats.set(SegmentationConfig_1.PredictionColumn, srcColumn?.customFormat.copyWithAutoGeneratedTrue());
            }
            newColumnFormats.set(SegmentationConfig_1.ProbabilityColumn, new CustomFormat(ColumnFormat.Percentage, 0, DefaultSettings.thousandSeparator, true));
            segExp.initializeColumnFormats().setColumnFormats(newColumnFormats).setColumnFormats(this.customFormats);
        }
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationState.BAD_METADATA;
        }
        let hasTarget = false;
        let hasFeature = false;
        for (const column of source) {
            // Invalid if the source has a column that conflicts with the alias
            if (column.name === this.alias) {
                return ToolValidationState.BAD_METADATA;
            }
            if (column.name === this.target) {
                hasTarget = true;
            }
            else if (this.excludeColumns === undefined || !this.excludeColumns.includes(column.name)) {
                // Assuming any column other than the target is a feature column.
                // Not exactly correct since string columns with distinct values > 300 are excluded.
                // It's not possible to verify here so assume it's okay.
                hasFeature = true;
            }
            // Valid if source has the target column and a feature column
            if (hasTarget && hasFeature) {
                return ToolValidationState.VALID;
            }
        }
        return ToolValidationState.BAD_METADATA;
    }
};
SegmentationConfig.SegmentIDColumn = 'SegmentID';
SegmentationConfig.NameColumn = 'Name';
SegmentationConfig.DescriptionColumn = 'Description';
SegmentationConfig.PredictionColumn = 'Prediction';
SegmentationConfig.ProbabilityColumn = 'Probability';
SegmentationConfig.FeatureColumn = 'Column';
SegmentationConfig.OperatorColumn = 'Operator';
SegmentationConfig.ValueColumn = 'Value';
SegmentationConfig = SegmentationConfig_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.SEGMENTATION)
], SegmentationConfig);
export { SegmentationConfig };
//# sourceMappingURL=segmentationConfig.js.map